import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./../../../assets/css/mentor/mentordashboard/dashboard.css";
import MyMasterLayout from "./../../../layouts/Mentor/MyMasterlayout";

const Dashboard = ({
  totalRequests,
  pendingRequests,
  myStudents,
  isVerified = false,
}) => {
  const navigate = useNavigate();
  const [showVerifyTooltip, setShowVerifyTooltip] = useState(false);

  // ── localStorage se mentor name ──
  const mentorData = JSON.parse(localStorage.getItem("mentorData") || "{}");
  const firstname = mentorData?.firstname || "";
  const email = mentorData?.email || "";
  const displayName = firstname || email.split("@")[0] || "Mentor";

  // ==================== ASSIGNED RESOURCES ====================
  const assignedResources = [
    {
      id: 1,
      title: "MSc Advanced Mechanical Engineering",
      students: 24,
      progress: 85,
      color: "#4a90e2",
      icon: "bi-code-slash",
    },
    {
      id: 2,
      title: "Introduction to Python Programming",
      students: 18,
      progress: 92,
      color: "#27ae60",
      icon: "bi-file-code",
    },
    {
      id: 3,
      title: "Data Science Workshop",
      students: 31,
      progress: 67,
      color: "#f39c12",
      icon: "bi-graph-up",
    },
    {
      id: 4,
      title: "Web Development Bootcamp",
      students: 15,
      progress: 78,
      color: "#e74c3c",
      icon: "bi-laptop",
    },
  ];

  // ==================== ONGOING SESSIONS ====================
  const ongoingSessions = [
    {
      id: 1,
      daysLeft: 1,
      date: "TUE, JUN 15",
      time: "@12:00PM",
      title: "Web Development Bootcamp – Live Q&A",
      type: "Online Session",
      courseColor: "#e74c3c",
      courseIcon: "bi-laptop",
    },
    {
      id: 2,
      daysLeft: 4,
      date: "THU, JUN 18",
      time: "@12:00PM",
      title: "Python Programming – Data Structures Deep Dive",
      type: "Online Session",
      courseColor: "#27ae60",
      courseIcon: "bi-file-code",
    },
    {
      id: 3,
      daysLeft: 6,
      date: "TUE, JUN 20",
      time: "@2:00PM",
      title: "MSc Mechanical Engineering – Project Review",
      type: "Offline Session",
      courseColor: "#4a90e2",
      courseIcon: "bi-code-slash",
    },
    {
      id: 4,
      daysLeft: 8,
      date: "FRI, JUN 23",
      time: "@10:00AM",
      title: "Data Science Workshop – Model Evaluation",
      type: "Online Session",
      courseColor: "#f39c12",
      courseIcon: "bi-graph-up",
    },
    {
      id: 5,
      daysLeft: 10,
      date: "MON, JUN 26",
      time: "@3:00PM",
      title: "Web Development – Final Project Presentations",
      type: "Offline Session",
      courseColor: "#e74c3c",
      courseIcon: "bi-laptop",
    },
  ];

  const [sessionIndex, setSessionIndex] = useState(0);
  const visibleCount = 3;

  const handlePrev = () => setSessionIndex((i) => Math.max(0, i - 1));
  const handleNext = () =>
    setSessionIndex((i) =>
      Math.min(ongoingSessions.length - visibleCount, i + 1),
    );
  const visibleSessions = ongoingSessions.slice(
    sessionIndex,
    sessionIndex + visibleCount,
  );

  const handleAttend = (sessionId) => navigate(`/mentor/sessions/${sessionId}`);

  // ==================== MONTHLY PROGRESS ====================
  const monthlyProgress = {
    completed: 18,
    pending: 6,
    cancelled: 2,
    total: 26,
  };

  // ==================== WEEKLY ACTIVITY ====================
  const weeklyActivity = [
    { day: "SUN", sessions: 3, hours: 4.5 },
    { day: "MON", sessions: 5, hours: 7.5 },
    { day: "TUE", sessions: 4, hours: 6.0 },
    { day: "WED", sessions: 7, hours: 10.5 },
    { day: "THU", sessions: 6, hours: 9.0 },
    { day: "FRI", sessions: 8, hours: 12.0 },
    { day: "SAT", sessions: 2, hours: 3.0 },
  ];

  // ==================== STUDENT PERFORMANCE ====================
  const studentPerformance = [
    { day: "SUN", avgScore: 72, activeStudents: 15 },
    { day: "MON", avgScore: 85, activeStudents: 28 },
    { day: "TUE", avgScore: 78, activeStudents: 24 },
    { day: "WED", avgScore: 92, activeStudents: 35 },
    { day: "THU", avgScore: 88, activeStudents: 31 },
    { day: "FRI", avgScore: 95, activeStudents: 38 },
    { day: "SAT", avgScore: 80, activeStudents: 20 },
  ];

  const maxSessions = Math.max(...weeklyActivity.map((d) => d.sessions));
  const maxScore = Math.max(...studentPerformance.map((d) => d.avgScore));

  const generatePath = (data, dataKey, maxValue, height) => {
    const points = data.map((item, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = height - (item[dataKey] / maxValue) * height;
      return `${x},${y}`;
    });
    return `M ${points.join(" L ")}`;
  };

  const generateAreaPath = (data, dataKey, maxValue, height) => {
    const points = data.map((item, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = height - (item[dataKey] / maxValue) * height;
      return `${x},${y}`;
    });
    return `M 0,${height} L ${points.join(" L ")} L 100,${height} Z`;
  };

  return (
    <MyMasterLayout>
      <div className="mdb-dashboard-container">
        {/* ==================== WELCOME BAR ==================== */}
        <div className="mdb-welcome-bar">
          <h2 className="mdb-welcome-title">
            Welcome back,{" "}
            <span className="mdb-welcome-name">{displayName}</span> 👋
          </h2>
          <p className="mdb-welcome-sub">
            Here's what's happening with your students today.
          </p>
        </div>

        {/* ==================== STATS CARDS ==================== */}
        <div className="mdb-stats-grid">
          <div className="mdb-stat-card mdb-stat-gradient-blue">
            <div className="mdb-stat-content">
              <div className="mdb-stat-icon">
                <i className="bi bi-clipboard-data"></i>
              </div>
              <div className="mdb-stat-info">
                <div className="mdb-stat-label">Total Requests</div>
                <div className="mdb-stat-value">{totalRequests}</div>
              </div>
            </div>
          </div>
          <div className="mdb-stat-card mdb-stat-gradient-orange">
            <div className="mdb-stat-content">
              <div className="mdb-stat-icon">
                <i className="bi bi-hourglass-split"></i>
              </div>
              <div className="mdb-stat-info">
                <div className="mdb-stat-label">Pending Requests</div>
                <div className="mdb-stat-value">{pendingRequests}</div>
              </div>
            </div>
          </div>
          <div className="mdb-stat-card mdb-stat-gradient-green">
            <div className="mdb-stat-content">
              <div className="mdb-stat-icon">
                <i className="bi bi-people-fill"></i>
              </div>
              <div className="mdb-stat-info">
                <div className="mdb-stat-label">My Students</div>
                <div className="mdb-stat-value">{myStudents}</div>
              </div>
            </div>
          </div>
          <div className="mdb-stat-card mdb-stat-gradient-red">
            <div className="mdb-stat-content">
              <div className="mdb-stat-icon">
                <i className="bi bi-calendar-check"></i>
              </div>
              <div className="mdb-stat-info">
                <div className="mdb-stat-label">Sessions This Month</div>
                <div className="mdb-stat-value">24</div>
              </div>
            </div>
          </div>
        </div>

        {/* ==================== TWO COLUMN LAYOUT ==================== */}
        <div className="mdb-content-grid">
          {/* ── LEFT COLUMN ── */}
          <div className="mdb-left-column">
            {/* ONGOING INFO SESSIONS */}
            <div className="mdb-card">
              <div className="mdb-card-header">
                <h2 className="mdb-section-title">
                  <i className="bi bi-camera-video"></i> Ongoing Info Sessions
                </h2>
              </div>
              <div className="mdb-ois-wrapper">
                <button
                  className="mdb-ois-arrow mdb-ois-arrow-left"
                  onClick={handlePrev}
                  disabled={sessionIndex === 0}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <div className="mdb-ois-cards">
                  {visibleSessions.map((session) => (
                    <div key={session.id} className="mdb-ois-card">
                      <div className="mdb-ois-top-row">
                        <div
                          className="mdb-ois-course-icon"
                          style={{ background: session.courseColor }}
                        >
                          <i className={`bi ${session.courseIcon}`}></i>
                        </div>
                        <div className="mdb-ois-days-left">
                          <i className="bi bi-clock"></i>
                          <span>
                            {session.daysLeft}{" "}
                            {session.daysLeft === 1 ? "Day" : "Days"} Left
                          </span>
                        </div>
                      </div>
                      <div className="mdb-ois-datetime">
                        {session.date} <span>{session.time}</span>
                      </div>
                      <h4 className="mdb-ois-title">{session.title}</h4>
                      <div className="mdb-ois-type">
                        <i
                          className={
                            session.type === "Online Session"
                              ? "bi bi-wifi"
                              : "bi bi-geo-alt"
                          }
                        ></i>
                        <span>{session.type}</span>
                      </div>
                      <button
                        className="mdb-ois-attend-btn"
                        onClick={() => handleAttend(session.id)}
                      >
                        Attend
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  className="mdb-ois-arrow mdb-ois-arrow-right"
                  onClick={handleNext}
                  disabled={
                    sessionIndex >= ongoingSessions.length - visibleCount
                  }
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>

            {/* WEEKLY ACTIVITY CHART */}
            <div className="mdb-card">
              <div className="mdb-card-header">
                <div>
                  <h2 className="mdb-section-title">
                    <i className="bi bi-activity"></i> Weekly Activity
                  </h2>
                  <p className="mdb-chart-description">
                    Sessions and hours this week
                  </p>
                </div>
                <div className="mdb-chart-stats">
                  <div className="mdb-chart-stat-item">
                    <span className="mdb-chart-stat-label">Total Sessions</span>
                    <span className="mdb-chart-stat-value">
                      {weeklyActivity.reduce((sum, d) => sum + d.sessions, 0)}
                    </span>
                  </div>
                  <div className="mdb-chart-stat-item">
                    <span className="mdb-chart-stat-label">Total Hours</span>
                    <span className="mdb-chart-stat-value">
                      {weeklyActivity.reduce((sum, d) => sum + d.hours, 0)}h
                    </span>
                  </div>
                </div>
              </div>
              <div className="mdb-line-chart-container">
                <svg
                  className="mdb-line-chart"
                  viewBox="0 0 100 60"
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient
                      id="areaGradient"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        style={{ stopColor: "#781c2e", stopOpacity: 0.3 }}
                      />
                      <stop
                        offset="100%"
                        style={{ stopColor: "#781c2e", stopOpacity: 0.05 }}
                      />
                    </linearGradient>
                  </defs>
                  <path
                    d={generateAreaPath(
                      weeklyActivity,
                      "sessions",
                      maxSessions,
                      60,
                    )}
                    fill="url(#areaGradient)"
                  />
                  <path
                    className="mdb-line-stroke"
                    d={generatePath(
                      weeklyActivity,
                      "sessions",
                      maxSessions,
                      60,
                    )}
                    fill="none"
                    stroke="#781c2e"
                    strokeWidth="0.8"
                  />
                  {weeklyActivity.map((item, index) => {
                    const x = (index / (weeklyActivity.length - 1)) * 100;
                    const y = 60 - (item.sessions / maxSessions) * 60;
                    return (
                      <circle
                        key={index}
                        cx={x}
                        cy={y}
                        r="1.2"
                        fill="#781c2e"
                        className="mdb-chart-dot"
                      />
                    );
                  })}
                </svg>
                <div className="mdb-chart-labels">
                  {weeklyActivity.map((item, index) => (
                    <div key={index} className="mdb-chart-label-item">
                      <span className="mdb-chart-day">{item.day}</span>
                      <span className="mdb-chart-value">{item.sessions}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* STUDENT PERFORMANCE CHART */}
            <div className="mdb-card">
              <div className="mdb-card-header">
                <div>
                  <h2 className="mdb-section-title">
                    <i className="bi bi-graph-up-arrow"></i> Student Performance
                  </h2>
                  <p className="mdb-chart-description">
                    Average scores and active students
                  </p>
                </div>
                <div className="mdb-chart-stats">
                  <div className="mdb-chart-stat-item">
                    <span className="mdb-chart-stat-label">Avg Score</span>
                    <span className="mdb-chart-stat-value">
                      {Math.round(
                        studentPerformance.reduce(
                          (sum, d) => sum + d.avgScore,
                          0,
                        ) / studentPerformance.length,
                      )}
                      %
                    </span>
                  </div>
                  <div className="mdb-chart-stat-item">
                    <span className="mdb-chart-stat-label">
                      Active Students
                    </span>
                    <span className="mdb-chart-stat-value">
                      {studentPerformance.reduce(
                        (sum, d) => sum + d.activeStudents,
                        0,
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mdb-line-chart-container">
                <svg
                  className="mdb-line-chart"
                  viewBox="0 0 100 60"
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient
                      id="performanceGradient"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        style={{ stopColor: "#4a90e2", stopOpacity: 0.3 }}
                      />
                      <stop
                        offset="100%"
                        style={{ stopColor: "#4a90e2", stopOpacity: 0.05 }}
                      />
                    </linearGradient>
                  </defs>
                  <path
                    d={generateAreaPath(
                      studentPerformance,
                      "avgScore",
                      maxScore,
                      60,
                    )}
                    fill="url(#performanceGradient)"
                  />
                  <path
                    className="mdb-line-stroke"
                    d={generatePath(
                      studentPerformance,
                      "avgScore",
                      maxScore,
                      60,
                    )}
                    fill="none"
                    stroke="#4a90e2"
                    strokeWidth="0.8"
                  />
                  {studentPerformance.map((item, index) => {
                    const x = (index / (studentPerformance.length - 1)) * 100;
                    const y = 60 - (item.avgScore / maxScore) * 60;
                    return (
                      <circle
                        key={index}
                        cx={x}
                        cy={y}
                        r="1.2"
                        fill="#4a90e2"
                        className="mdb-chart-dot"
                      />
                    );
                  })}
                </svg>
                <div className="mdb-chart-labels">
                  {studentPerformance.map((item, index) => (
                    <div key={index} className="mdb-chart-label-item">
                      <span className="mdb-chart-day">{item.day}</span>
                      <span className="mdb-chart-value">{item.avgScore}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="mdb-right-column">
            {/* MY PROGRESS RING */}
            <div className="mdb-card">
              <div className="mdb-card-header">
                <h2 className="mdb-section-title">
                  <i className="bi bi-pie-chart"></i> My Progress
                </h2>
              </div>
              <div className="mdb-progress-section">
                <div className="mdb-progress-ring-container">
                  <svg className="mdb-progress-ring" width="180" height="180">
                    <circle
                      className="mdb-progress-ring-bg"
                      cx="90"
                      cy="90"
                      r="75"
                    />
                    <circle
                      className="mdb-progress-ring-fill"
                      cx="90"
                      cy="90"
                      r="75"
                      style={{
                        strokeDasharray: `${(monthlyProgress.completed / monthlyProgress.total) * 471} 471`,
                      }}
                    />
                  </svg>
                  <div className="mdb-progress-center">
                    <span className="mdb-progress-number">
                      {monthlyProgress.completed}
                    </span>
                    <span className="mdb-progress-total">
                      / {monthlyProgress.total}
                    </span>
                    <span className="mdb-progress-label">Sessions</span>
                  </div>
                </div>
                <div className="mdb-progress-stats">
                  <div className="mdb-progress-stat-item">
                    <div className="mdb-stat-dot mdb-dot-completed"></div>
                    <span className="mdb-stat-name">Completed</span>
                    <span className="mdb-stat-number">
                      {monthlyProgress.completed}
                    </span>
                  </div>
                  <div className="mdb-progress-stat-item">
                    <div className="mdb-stat-dot mdb-dot-pending"></div>
                    <span className="mdb-stat-name">Pending</span>
                    <span className="mdb-stat-number">
                      {monthlyProgress.pending}
                    </span>
                  </div>
                  <div className="mdb-progress-stat-item">
                    <div className="mdb-stat-dot mdb-dot-cancelled"></div>
                    <span className="mdb-stat-name">Cancelled</span>
                    <span className="mdb-stat-number">
                      {monthlyProgress.cancelled}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* MY COURSES */}
            <div className="mdb-card">
              <div className="mdb-card-header">
                <h2 className="mdb-section-title">
                  <i className="bi bi-mortarboard"></i> My Courses
                </h2>
                <button className="mdb-see-all-btn">See All</button>
              </div>
              <div className="mdb-courses-list">
                {assignedResources.map((course) => (
                  <div key={course.id} className="mdb-course-card">
                    <div
                      className="mdb-course-icon-wrapper"
                      style={{ background: course.color }}
                    >
                      <i className={`bi ${course.icon}`}></i>
                    </div>
                    <div className="mdb-course-info">
                      <h4 className="mdb-course-title">{course.title}</h4>
                      <div className="mdb-course-meta">
                        <span className="mdb-student-badge">
                          <i className="bi bi-people"></i> {course.students}{" "}
                          Students
                        </span>
                      </div>
                      <div className="mdb-progress-bar-container">
                        <div className="mdb-progress-bar-bg">
                          <div
                            className="mdb-progress-bar-fill"
                            style={{
                              width: `${course.progress}%`,
                              background: course.color,
                            }}
                          ></div>
                        </div>
                        <span className="mdb-progress-percent">
                          {course.progress}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== FLOATING VERIFICATION BUTTON ==================== */}
      {!isVerified && (
        <div className="mdb-fab-wrapper">
          {showVerifyTooltip && (
            <div className="mdb-fab-tooltip">
              <i className="bi bi-info-circle"></i>
              Your account is <strong>not verified</strong> yet.
              <br />
              Click to submit for admin verification.
            </div>
          )}
          <button
            className="mdb-fab-btn"
            onClick={() => navigate("/mentor/Application")}
            onMouseEnter={() => setShowVerifyTooltip(true)}
            onMouseLeave={() => setShowVerifyTooltip(false)}
          >
            <span className="mdb-fab-pulse"></span>
            <i className="bi bi-patch-check-fill mdb-fab-icon"></i>
            <span className="mdb-fab-label">Get Verified</span>
          </button>
        </div>
      )}
    </MyMasterLayout>
  );
};

export default Dashboard;
